<script>
import FormItemGroup from '@components/form-item-group.vue'
import FormItem from '@components/form-item.vue'
import AdditionalLoPhaseTwoLicensesForm from '@components/personnel/additional-lo-phase-two-licenses-form.vue'
import { saveOfficeDetails } from '@src/api/personnelManagement'
import { authComputed } from '@state/helpers'
import _ from 'lodash'
import { transform as transformOffice } from '@src/api/transformers/officeTransformer'
import { arrayToSentence, parseErrorMessage } from '@utils/string-helpers'
import PersonnelFormLicenseStates from '@components/personnel/personnel-form-license-states.vue'
import { states } from '@utils/consts/states'

export default {
  components: {
    PersonnelFormLicenseStates,
    FormItemGroup,
    FormItem,
    AdditionalLoPhaseTwoLicensesForm,
  },
  props: {
    form: {
      type: String,
      required: true,
    },
    officeToEdit: {
      type: [Object, null],
      default: null,
    },
    customClass: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      error: false,
      submitting: false,
      selectedWebAdminId: 0,
      office: null,
    }
  },
  computed: {
    ...authComputed,
    users() {
      return this.office ? this.office.users.filter((x) => !x.isInactive && x.status !== 'created') : []
    },
    loPhaseTwoUsers() {
      return this.users.filter((x) => x.isLoPhaseTwo && x.status === 'active')
    },
    additionalLoPhaseTwoLicenseStates() {
      const originalLicenses = this.office.originalLicenses.map((x) => x.state)
      const allLicenses = this.office.licenses.map((x) => x.state)
      return _.difference(allLicenses, originalLicenses)
    },
    getTitle() {
      let form = ''
      if (this.form === 'request_licenses') {
        form = 'Licensing'
      }

      if (this.form === 'website') {
        form = 'Website'
      }

      return `${this.office?.tradeName} - ${form}`
    },
    hasOfficeChanged() {
      return !!this.getChangedFields('officePmFormModal').length
    },
    usersInAlphabeticalOrder() {
      return _.orderBy(this.users, 'name')
    },
    eligibleWebAdmins() {
      const users = this.usersInAlphabeticalOrder
        .filter((x) => x.status === 'active')
        .map((x) => ({
          label: x.name,
          value: x.id.toString(),
        }))

      if (!this.selectedWebAdminId) {
        return [
          {
            label: 'Add a User',
            value: null,
          },
          ...users,
        ]
      }

      return [...users]
    },
    getSelectedWebAdmin() {
      return _.find(this.office.users, { id: parseInt(this.selectedWebAdminId) }) || null
    },
    availableStates() {
      const amountOfStates = this.office.licenses.length
      const hasDCLicense = this.office.licenses.some((x) => x.state === 'DC')
      let availableStates = states
      if (amountOfStates >= 4) {
        availableStates = this.office.licenses.map((x) => x.state)
      }

      if (amountOfStates === 3 && !hasDCLicense) {
        availableStates = [...this.office.licenses.map((x) => x.state), 'DC']
      }

      return availableStates.filter((x) => !this.office.originalLicenses.some((y) => y.state === x))
    },
  },
  watch: {
    selectedWebAdminId() {
      this.office.webAdmin = this.getSelectedWebAdmin
    },
  },
  methods: {
    arrayToSentence,
    loadOfficeVariable() {
      this.office = _.cloneDeep(this.officeToEdit)
      if (this.office) {
        this.selectedWebAdminId = this.office.webAdmin?.id?.toString()
      }
    },
    async saveChanges() {
      try {
        if (this.$refs?.loPhaseTwoLicensesForm?.emptyAdditionalLoPhaseTwoLicensesIds().length) {
          const names = this.$refs?.loPhaseTwoLicensesForm?.emptyAdditionalLoPhaseTwoLicensesIds().map((id) => {
            const user = _.find(this.office.users, { id })
            return user.name
          })
          this.$bvToast.toast(`Please select a state license for ${arrayToSentence(names)}`, {
            toaster: 'b-toaster-bottom-center',
            toastClass: 'toast-error',
          })
          return
        }

        this.submitting = true
        const officeDetails = {
          phone: this.office.phone,
          website: this.office.website,
          locationDetails: this.office.locationDetails,
          shortDescription: this.office.shortDescription,
          webAdmin: this.getSelectedWebAdmin,
          licenses: _.orderBy(this.office.originalLicenses, 'state'),
          requestedLicenses: _.orderBy(this.office.licenses, 'state'),
          loPhaseTwoLicenses: this.$refs?.loPhaseTwoLicensesForm?.getAdditionalLoPhaseTwoLicenses(),
        }

        const isLicensesChanged = !_.isEqual(
          _.orderBy(this.office.originalLicenses, 'state'),
          _.orderBy(this.office.licenses, 'state')
        )

        let office = null
        if (isLicensesChanged) {
          const officeFresh = await this.$bvModal
            .msgBoxConfirm(
              `This request will be sent to the Motto Product Support team to review.  Do you want to continue?`,
              {
                modalClass: 'confirmation-modal',
                title: 'Confirm',
                okTitle: 'Continue',
                cancelTitle: 'No',
                centered: true,
              }
            )
            .then(async (confirmation) => {
              if (confirmation) {
                const { data: office } = await saveOfficeDetails({
                  id: this.office.id,
                  officeDetails,
                })

                return office
              }

              return false
            })

          if (!officeFresh) {
            return false
          }

          office = officeFresh
        } else {
          const { data: officeFresh } = await saveOfficeDetails({
            id: this.office.id,
            officeDetails,
          })

          office = officeFresh
        }

        this.$bvToast.toast(
          isLicensesChanged
            ? `State License request has been submitted and is under review by Motto Product Support`
            : 'Office updated successfully',
          {
            toaster: 'b-toaster-bottom-center',
            toastClass: 'toast-success',
          }
        )
        return office
      } catch (err) {
        const errorMessage = parseErrorMessage(err)
        this.$bvToast.toast(errorMessage, {
          toaster: 'b-toaster-bottom-center',
          toastClass: 'toast-error',
        })
      } finally {
        this.submitting = false
      }
    },
    async handlePmFormCancel(bvCancelEvent) {
      bvCancelEvent.preventDefault()
      if (this.form === 'request_licenses') {
        await this.$bvModal
          .msgBoxConfirm(`Are you sure you wish to cancel the request for a State License?`, {
            modalClass: 'confirmation-modal',
            title: 'Confirm',
            okTitle: 'Yes',
            cancelTitle: 'No',
            centered: true,
          })
          .then(async (confirmation) => {
            if (confirmation) {
              this.$bvModal.hide('officeModalPmForm')
            }
          })
      } else {
        this.$nextTick(() => {
          this.$bvModal.hide('officeModalPmForm')
        })
      }
    },
    getChangedFields(formRef) {
      if (!this.$refs[formRef]) {
        return []
      }
      return Object.keys(this.$refs[formRef].fields)
        .filter((key) => this.$refs[formRef].fields[key].changed)
        .sort()
    },
    async handlePmFormOK(bvOkEvent) {
      bvOkEvent.preventDefault()
      const office = await this.saveChanges()
      if (!office) {
        return
      }
      this.$emit('update-office', {
        office: transformOffice(office),
      })
      this.$bvModal.hide('officeModalPmForm')
    },
  },
}
</script>

<template>
  <div>
    <ValidationObserver ref="officePmFormModal" v-slot="{ failed: validationFailed }" class="d-contents">
      <b-modal
        id="officeModalPmForm"
        :title="getTitle"
        :modal-class="customClass ? `${customClass} form-modal` : `form-modal`"
        cancel-title="Cancel"
        :cancel-disabled="submitting"
        ok-title="Submit"
        :centered="true"
        :ok-disabled="!office || validationFailed || submitting || !hasOfficeChanged"
        @cancel="handlePmFormCancel"
        @ok="handlePmFormOK"
        @show="loadOfficeVariable">
        <div v-if="office">
          <div v-if="form === 'website'">
            <FormItemGroup>
              <FormItem label="Website" :disabled="submitting" :value.sync="office.website" />
            </FormItemGroup>
            <FormItemGroup>
              <FormItem label="Vanity Url" :disabled="submitting" :value.sync="office.vanityUrl" />
            </FormItemGroup>
            <FormItemGroup>
              <FormItem
                label="Web Admin"
                :value.sync="selectedWebAdminId"
                edit-mode
                :disabled="submitting"
                :edit-select-values="eligibleWebAdmins"
                tooltip-text="The Website Admin designation grants this user access to the main office website admin settings.  Only one user may be designated as the Website Admin at this time." />
            </FormItemGroup>
            <FormItemGroup>
              <FormItem
                label="ORM Business Description"
                :value.sync="office.locationDetails"
                edit-mode
                textarea
                :options="{ hideAsterisk: true }"
                :disabled="submitting"
                tooltip-text="This description appears on the main office website in the About Us section, and on listings such as Google My Business. "
                :rules="'required|max:750'" />
            </FormItemGroup>
            <FormItemGroup>
              <FormItem
                label="Short Web Description"
                :value.sync="office.shortDescription"
                rules="max:100"
                edit-mode
                :disabled="submitting"
                tooltip-text="Add a short description of your business that will appear across the web where a short business description is available with your populated information.  Limit is 100 characters." />
            </FormItemGroup>
          </div>
          <div v-if="form === 'request_licenses'">
            <div>
              <div>
                <FormItemGroup>
                  <PersonnelFormLicenseStates
                    label="State(s) of Licensure"
                    edit-mode
                    :rules="'required'"
                    :edit-select-values="availableStates"
                    :disabled="!office.canAddMoreLicenses || submitting"
                    :placeholder="'Select...'"
                    :read-only-values="office.originalLicenses"
                    :value.sync="office.licenses" />
                </FormItemGroup>
              </div>
              <AdditionalLoPhaseTwoLicensesForm
                ref="loPhaseTwoLicensesForm"
                :phase-two-users="loPhaseTwoUsers"
                :submitting="submitting"
                :license-states="additionalLoPhaseTwoLicenseStates" />
            </div>
          </div>
        </div>
      </b-modal>
    </ValidationObserver>
  </div>
</template>

<style lang="scss" module>
@import '@design';

.keywordsInput {
  max-width: 350px;
}
</style>
